"use client"

import type React from "react"
import { useState, useRef, useCallback } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Slider } from "@/components/ui/slider"
import { FileUp, Download, ImageIcon, Loader2, AlertCircle, RefreshCw } from "lucide-react"
import { toast } from "sonner"
import { Progress } from "@/components/ui/progress"
import { API_URL, API_KEY } from "@/lib/api-config"

const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024 // 50MB

type ProcessingStatus = "idle" | "creating-job" | "uploading" | "processing" | "completed" | "error"

export function HeicConverter() {
  const [file, setFile] = useState<File | null>(null)
  const [quality, setQuality] = useState(90)
  const [outputFileName, setOutputFileName] = useState("converted")

  const [status, setStatus] = useState<ProcessingStatus>("idle")
  const [uploadProgress, setUploadProgress] = useState(0)
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null)
  const [resultInfo, setResultInfo] = useState<{ fileName: string; originalSize: number; convertedSize: number } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return

    const validExtensions = [".heic", ".heif"]
    const ext = selectedFile.name.toLowerCase().slice(selectedFile.name.lastIndexOf("."))

    if (!validExtensions.includes(ext)) {
      toast.error("Please select a HEIC or HEIF file")
      return
    }

    if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`File size exceeds ${MAX_FILE_SIZE_BYTES / 1024 / 1024}MB limit`)
      return
    }

    setFile(selectedFile)
    setDownloadUrl(null)
    setResultInfo(null)
    setError(null)

    const baseName = selectedFile.name.replace(/\.(heic|heif)$/i, "")
    setOutputFileName(baseName)

    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }, [])

  const clearFile = useCallback(() => {
    setFile(null)
    setDownloadUrl(null)
    setResultInfo(null)
    setError(null)
    setQuality(90)
    setOutputFileName("converted")
  }, [])

  const convertHeic = async () => {
    if (!file) {
      toast.error("Please select a HEIC file")
      return
    }

    setStatus("creating-job")
    setError(null)
    setUploadProgress(0)

    try {
      // Step 1: Create job and get upload URL
      const createResponse = await fetch(`${API_URL}/api/v1/image/heic/convert`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": API_KEY,
        },
        body: JSON.stringify({
          fileName: file.name,
          fileSizeBytes: file.size,
          quality,
          outputFileName,
        }),
      })

      if (!createResponse.ok) {
        const errorData = await createResponse.json().catch(() => ({}))
        throw new Error(errorData.detail || "Failed to create convert job")
      }

      const { jobId, uploadUrl } = await createResponse.json() as { jobId: string; uploadUrl: string }

      // Step 2: Upload HEIC
      setStatus("uploading")

      const xhr = new XMLHttpRequest()
      await new Promise<void>((resolve, reject) => {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setUploadProgress((e.loaded / e.total) * 100)
          }
        }
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve()
          } else {
            reject(new Error("Upload failed"))
          }
        }
        xhr.onerror = () => reject(new Error("Upload failed"))
        xhr.open("PUT", uploadUrl)
        xhr.setRequestHeader("Content-Type", "image/heic")
        xhr.send(file)
      })

      // Step 3: Process HEIC
      setStatus("processing")

      const processResponse = await fetch(`${API_URL}/api/v1/image/heic/convert/${jobId}/process`, {
        method: "POST",
        headers: {
          "X-API-Key": API_KEY,
        },
      })

      if (!processResponse.ok) {
        const errorData = await processResponse.json().catch(() => ({}))
        throw new Error(errorData.detail || "Failed to convert HEIC")
      }

      const result = await processResponse.json() as {
        downloadUrl: string
        fileName: string
        originalSizeBytes: number
        convertedSizeBytes: number
      }

      setDownloadUrl(result.downloadUrl)
      setResultInfo({
        fileName: result.fileName,
        originalSize: result.originalSizeBytes,
        convertedSize: result.convertedSizeBytes,
      })
      setStatus("completed")
      toast.success("HEIC converted to JPG successfully!")
    } catch (err) {
      setStatus("error")
      const message = err instanceof Error ? err.message : "An error occurred"
      setError(message)
      toast.error(message)
    }
  }

  const downloadImage = () => {
    if (!downloadUrl) return
    window.open(downloadUrl, "_blank")
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
  }

  const isProcessing = status === "creating-job" || status === "uploading" || status === "processing"

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RefreshCw className="h-5 w-5" />
          HEIC to JPG Converter
        </CardTitle>
        <CardDescription>
          Convert Apple HEIC/HEIF images to universally compatible JPG format.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* File Input */}
        <div className="space-y-4">
          <Input
            ref={fileInputRef}
            type="file"
            accept=".heic,.heif,image/heic,image/heif"
            onChange={handleFileSelect}
            className="hidden"
            id="heic-upload"
            disabled={isProcessing}
          />

          {!file ? (
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              className="w-full h-32 border-dashed"
              disabled={isProcessing}
            >
              <div className="flex flex-col items-center gap-2">
                <FileUp className="h-8 w-8" />
                <span>Select HEIC/HEIF File</span>
                <span className="text-xs text-muted-foreground">Max {MAX_FILE_SIZE_BYTES / 1024 / 1024}MB</span>
              </div>
            </Button>
          ) : (
            <div className="flex items-center gap-3 p-4 border rounded-lg bg-muted/50">
              <ImageIcon className="h-8 w-8 text-purple-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{file.name}</p>
                <p className="text-sm text-muted-foreground">{formatFileSize(file.size)}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={clearFile} disabled={isProcessing}>
                Clear
              </Button>
            </div>
          )}
        </div>

        {/* Options */}
        <div className="space-y-4">
          <div className="space-y-3">
            <div className="flex justify-between">
              <Label htmlFor="quality">JPG Quality</Label>
              <span className="text-sm text-muted-foreground">{quality}%</span>
            </div>
            <Slider
              id="quality"
              min={10}
              max={100}
              step={5}
              value={[quality]}
              onValueChange={([v]) => setQuality(v)}
              disabled={isProcessing}
            />
            <p className="text-xs text-muted-foreground">
              Higher quality = larger file size. 85-95% recommended for most uses.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="output-name">Output Filename</Label>
            <div className="flex items-center gap-2">
              <Input
                id="output-name"
                value={outputFileName}
                onChange={(e) => setOutputFileName(e.target.value)}
                placeholder="converted"
                disabled={isProcessing}
              />
              <span className="text-muted-foreground">.jpg</span>
            </div>
          </div>
        </div>

        {/* Progress */}
        {isProcessing && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">
                {status === "creating-job" && "Creating convert job..."}
                {status === "uploading" && `Uploading HEIC... ${Math.round(uploadProgress)}%`}
                {status === "processing" && "Converting to JPG..."}
              </span>
            </div>
            {status === "uploading" && <Progress value={uploadProgress} />}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Result */}
        {status === "completed" && resultInfo && (
          <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-green-800 dark:text-green-200">HEIC Converted Successfully!</p>
                <p className="text-sm text-green-600 dark:text-green-400">
                  {formatFileSize(resultInfo.originalSize)} → {formatFileSize(resultInfo.convertedSize)}
                </p>
              </div>
              <Button onClick={downloadImage}>
                <Download className="h-4 w-4 mr-2" />
                Download JPG
              </Button>
            </div>
          </div>
        )}

        {/* Convert Button */}
        <Button
          onClick={convertHeic}
          disabled={!file || isProcessing}
          className="w-full"
        >
          {isProcessing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4 mr-2" />
              Convert to JPG
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  )
}
